import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MsalProvider, AuthenticatedTemplate, useMsal } from '@azure/msal-react';
import { FluentProvider, webLightTheme, Spinner, makeStyles } from '@fluentui/react-components';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './telemetry/appInsights';
import { Header } from './components/Header';
import { AppLayout } from './components/AppLayout';
import { ErrorPage } from './components/ErrorPage';
import { FirstRunExperience } from './components/FirstRunExperience';
import { NoAccessProvider } from './context';
import { useFRE } from './services';
import {
  getLoginRequest,
  AuthProvider,
  getAuthProvider,
  setAuthProvider,
  clearAuthProvider,
  isTestMode,
} from './config';
import { PublicClientApplication } from '@azure/msal-browser';

import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from './telemetry/appInsights';

import { useFixTabsterA11y } from './hooks/useFixTabsterA11y';
import {
  clearStoredImpersonationEmail,
  getImpersonationEmail,
  storeImpersonationEmail,
} from './utils/impersonation';
import './styles/App.css';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    textAlign: 'center',
    gap: '16px',
  },
  skipLink: {
    position: 'absolute',
    left: '-9999px',
    top: 'auto',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    zIndex: 1001,
    '&:focus': {
      position: 'fixed',
      top: '8px',
      left: '8px',
      width: 'auto',
      height: 'auto',
      padding: '12px 16px',
      backgroundColor: '#0078d4',
      color: '#ffffff',
      borderRadius: '4px',
      textDecoration: 'none',
      fontWeight: 600,
      fontSize: '14px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.26)',
    },
  },
});

/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent: React.FC<{ authProvider: AuthProvider }> = ({ authProvider }) => {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance, inProgress, accounts } = useMsal();
  const styles = useStyles();
  const [accountWithClaims, setAccountWithClaims] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'candidate' | 'manager'>('candidate');
  const [isRedirecting, setIsRedirecting] = useState(false);

  // First Run Experience state
  const { isOpen: isFREOpen, closeFRE, replayFRE } = useFRE();
  const replayTriggerRef = useRef<HTMLButtonElement | null>(null);

  const handleReplayFRE = useCallback((trigger: HTMLButtonElement) => {
    replayTriggerRef.current = trigger;
    replayFRE();
  }, [replayFRE]);

  const handleCloseFRE = useCallback(() => {
    const trigger = replayTriggerRef.current;
    replayTriggerRef.current = null;
    void closeFRE();

    if (trigger) {
      requestAnimationFrame(() => trigger.focus());
    }
  }, [closeFRE]);

  // Get the appropriate login request based on auth provider using the function
  // Memoize to prevent unnecessary re-renders
  const currentLoginRequest = React.useMemo(() => getLoginRequest(authProvider), [authProvider]);

  // Get the first account
  const account = accounts[0];

  // Single effect to handle all login scenarios
  useEffect(() => {
    // Don't do anything if already redirecting or MSAL is processing
    if (isRedirecting || inProgress !== 'none') {
      return;
    }

    const isLoggingOut = sessionStorage.getItem('noAccessError') !== null;
    if (isLoggingOut) {
      return;
    }

    // Case 1: No accounts - need to login
    if (accounts.length === 0) {
      console.log(`Redirecting to ${authProvider} login...`);
      setIsRedirecting(true);

      // Clear any cached MSAL interaction state that might have stale scopes
      const keysToRemove = Object.keys(sessionStorage).filter(
        (key) => key.startsWith('msal.') && key.includes('interaction')
      );
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      instance.loginRedirect(currentLoginRequest).catch((error) => {
        console.log('Login redirect error:', error);
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error(String(error)),
          severityLevel: SeverityLevel.Error,
          properties: { component: 'MainContent', action: 'loginRedirect', authProvider },
        });
        setIsRedirecting(false);
      });
      return;
    }

    // Case 2: Account exists but no claims yet - try to get claims
    if (account && !accountWithClaims) {
      // First check if account already has claims from the ID token
      const activeAccount = instance.getActiveAccount();
      if (activeAccount?.idTokenClaims) {
        console.log('Using active account with claims');
        setAccountWithClaims(activeAccount);
        return;
      }
      if (account?.idTokenClaims) {
        console.log('Using account with claims');
        setAccountWithClaims(account);
        return;
      }

      // Need to acquire token silently
      // Make sure we're using an account from the correct authority
      const accountsFromThisInstance = instance.getAllAccounts();
      const correctAccount =
        accountsFromThisInstance.find((acc) => {
          // Match by home account id or just use first one from this instance
          return acc.homeAccountId === account.homeAccountId;
        }) || accountsFromThisInstance[0];

      if (!correctAccount) {
        console.log('No account found in this MSAL instance, redirecting to login');
        setIsRedirecting(true);
        instance.loginRedirect(currentLoginRequest).catch((error) => {
          console.log('Login redirect error:', error);
          setIsRedirecting(false);
        });
        return;
      }

      console.log(`Acquiring token silently for ${authProvider}...`);
      instance
        .acquireTokenSilent({
          scopes: currentLoginRequest.scopes,
          account: correctAccount,
          forceRefresh: false,
        })
        .then((response) => {
          console.log('Token acquired, setting account with claims');
          setAccountWithClaims(response.account);
        })
        .catch((error) => {
          console.error('Silent token acquisition failed:', error);
          appInsights.trackException({
            exception: error instanceof Error ? error : new Error(String(error)),
            severityLevel: SeverityLevel.Warning,
            properties: { component: 'MainContent', action: 'acquireTokenSilent', authProvider },
          });
          // If silent fails, we need interactive login
          if (error.name === 'InteractionRequiredAuthError' || error.name === 'BrowserAuthError') {
            console.log('Interactive login required');
            setIsRedirecting(true);
            instance.loginRedirect(currentLoginRequest).catch((err) => {
              console.log('Login redirect error:', err);
              setIsRedirecting(false);
            });
          } else {
            // Just use the account as-is for other errors
            setAccountWithClaims(correctAccount);
          }
        });
    }
  }, [
    accounts.length,
    account,
    accountWithClaims,
    inProgress,
    instance,
    currentLoginRequest,
    authProvider,
    isRedirecting,
  ]);

  // Show loading while MSAL is processing or we don't have account with claims yet
  if (inProgress !== 'none' || (account && !accountWithClaims)) {
    return (
      <div className={styles.container}>
        <Spinner size="huge" />
      </div>
    );
  }

  // Set authenticated user in App Insights once we have claims
  if (accountWithClaims) {
    const claims = accountWithClaims.idTokenClaims as Record<string, any> | undefined;
    const userId = accountWithClaims.localAccountId || accountWithClaims.homeAccountId;
    const email = claims?.emails?.[0] || claims?.email || claims?.preferred_username || accountWithClaims.username || '';
    appInsights.setAuthenticatedUserContext(userId, email, true);
  }

  // Still loading - waiting for redirect
  if (accounts.length === 0) {
    return (
      <div className={styles.container}>
        <Spinner size="huge" />
      </div>
    );
  }

  return (
    <>
      <a href="#maincontent" className={styles.skipLink}>
        Skip to main content
      </a>
      <Header viewMode={viewMode} onViewModeChange={setViewMode} onReplayFRE={handleReplayFRE} />
      <AuthenticatedTemplate>
        {accountWithClaims?.idTokenClaims ? <AppLayout /> : null}
        <FirstRunExperience isOpen={isFREOpen} onClose={handleCloseFRE} />
      </AuthenticatedTemplate>
    </>
  );
};

/**
 * App wrapper that handles authentication provider selection
 */
const AppContent: React.FC<{
  b2cInstance: PublicClientApplication;
  corporateInstance: PublicClientApplication;
}> = ({ b2cInstance, corporateInstance }) => {
  // Fix tabster dummy elements that violate aria-hidden-focus accessibility rule
  useFixTabsterA11y();

  const [authProvider, setAuthProviderState] = useState<AuthProvider | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorMessage] = useState<string | null>(() => {
    // Check for stored error message from logout
    const storedError = sessionStorage.getItem('noAccessError');
    if (storedError) {
      sessionStorage.removeItem('noAccessError');
      return storedError;
    }
    return null;
  });

  // Check if user is already authenticated or has a provider preference
  useEffect(() => {
    const checkExistingAuth = async () => {
      // If we have an error message to show, don't auto-select a provider
      // User needs to see the error on the error page first
      if (errorMessage) {
        setIsInitialized(true);
        return;
      }

      // Check URL path for /auth/corporate (from B2C unified.html employee sign-in link)
      const path = window.location.pathname;
      if (path.startsWith('/auth/corporate')) {
        // Clear the URL path to the root to avoid issues on refresh
        // (The path will be used again when Azure AD redirects back)
        // Don't replace URL here - let MSAL handle the redirect

        // Set the provider and trigger login
        setAuthProvider('corporate');
        setAuthProviderState('corporate');
        setIsInitialized(true);
        return;
      }

      // An impersonation request always uses corporate authentication. The API
      // remains responsible for enforcing the flightedRequirements permission.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonationEmail = getImpersonationEmail(window.location.search);
      if (impersonationEmail) {
        storeImpersonationEmail(impersonationEmail);
        if (urlParams.has('provider')) {
          urlParams.delete('provider');
          const remainingQuery = urlParams.toString();
          const newUrl =
            window.location.pathname +
            (remainingQuery ? `?${remainingQuery}` : '') +
            window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
        }

        setAuthProvider('corporate');
        setAuthProviderState('corporate');
        setIsInitialized(true);
        return;
      }

      // Check URL query parameters for provider selection (legacy support)
      const providerParam = urlParams.get('provider');
      if (providerParam === 'corporate' || providerParam === 'b2c') {
        // Clear only the provider parameter so unrelated query parameters survive.
        urlParams.delete('provider');
        const remainingQuery = urlParams.toString();
        const newUrl =
          window.location.pathname +
          (remainingQuery ? `?${remainingQuery}` : '') +
          window.location.hash;
        window.history.replaceState({}, document.title, newUrl);

        // Set the provider and trigger login
        setAuthProvider(providerParam as AuthProvider);
        setAuthProviderState(providerParam as AuthProvider);
        setIsInitialized(true);
        return;
      }

      // Check if user explicitly logged out - force showing error page
      const forceSelection = sessionStorage.getItem('forceSignInSelection');
      if (forceSelection) {
        sessionStorage.removeItem('forceSignInSelection');
        setIsInitialized(true);
        return;
      }

      const storedProvider = getAuthProvider();

      // Check if we have accounts in either instance
      const b2cAccounts = b2cInstance.getAllAccounts();
      const corporateAccounts = corporateInstance.getAllAccounts();

      // If we have a stored provider, only check accounts for that provider
      // This prevents authority mismatch errors when switching between providers
      if (storedProvider === 'b2c' && b2cAccounts.length > 0) {
        setAuthProvider('b2c');
        setAuthProviderState('b2c');
      } else if (storedProvider === 'corporate' && corporateAccounts.length > 0) {
        setAuthProvider('corporate');
        setAuthProviderState('corporate');
      } else if (!storedProvider) {
        // No stored provider - check both instances first
        if (b2cAccounts.length > 0) {
          setAuthProvider('b2c');
          setAuthProviderState('b2c');
        } else if (corporateAccounts.length > 0) {
          setAuthProvider('corporate');
          setAuthProviderState('corporate');
        } else {
          // No accounts anywhere - automatically redirect to B2C
          // Employees can use "Sign in here" link on B2C unified page
          setAuthProvider('b2c');
          setAuthProviderState('b2c');
        }
      } else {
        // Stored provider but no matching accounts - user pressed back or login failed
        // Redirect to B2C automatically instead of showing selection
        setAuthProvider('b2c');
        setAuthProviderState('b2c');
      }

      setIsInitialized(true);
    };

    checkExistingAuth();
  }, [b2cInstance, corporateInstance, errorMessage]);

  // Handle no access error - log out user and show error on sign-in page
  const handleNoAccess = useCallback(
    async (message: string) => {
      console.log('No access error:', message);

      // Track no-access event in Application Insights
      appInsights.trackEvent(
        { name: 'NoAccessError' },
        { component: 'AppContent', message, authProvider },
      );

      // Store the error message - it will persist through the redirect
      sessionStorage.setItem('noAccessError', message);

      // Get current instance based on auth provider
      const currentInstance = authProvider === 'corporate' ? corporateInstance : b2cInstance;

      // Clear auth provider from storage
      clearAuthProvider();
      clearStoredImpersonationEmail();

      // Get the current account to pass to logoutRedirect
      // This ensures proper cleanup of the specific account
      const currentAccount = currentInstance.getActiveAccount() || currentInstance.getAllAccounts()[0];

      // Clear cache from both instances to ensure fresh login on return
      try {
        await b2cInstance.clearCache();
      } catch (e) {
        console.log('Error clearing B2C cache:', e);
      }
      try {
        await corporateInstance.clearCache();
      } catch (e) {
        console.log('Error clearing corporate cache:', e);
      }

      // For corporate users, skip the server-side logout to avoid the account picker
      // Just clear local cache and redirect directly to error page
      if (authProvider === 'corporate') {
        window.location.href = window.location.origin;
        return;
      }

      // For B2C users, do a proper logout to end the B2C session
      currentInstance
        .logoutRedirect({
          account: currentAccount,
          postLogoutRedirectUri: window.location.origin,
        })
        .catch((e) => {
          console.error('Logout redirect error:', e);
          appInsights.trackException({
            exception: e instanceof Error ? e : new Error(String(e)),
            severityLevel: SeverityLevel.Error,
            properties: { component: 'AppContent', action: 'logoutRedirect', authProvider },
          });
          // Fallback: reload the page to show error page
          window.location.href = window.location.origin;
        });
    },
    [authProvider, b2cInstance, corporateInstance]
  );

  if (!isInitialized) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Spinner size="huge" />
        </div>
      </FluentProvider>
    );
  }

  // Show error page if there's an error message (no data found)
  if (!authProvider && errorMessage) {
    return (
      <FluentProvider theme={webLightTheme}>
        <ErrorPage errorMessage={errorMessage ?? undefined} />
      </FluentProvider>
    );
  }

  // If no provider and no error, this shouldn't happen since we auto-redirect to B2C
  // But just in case, redirect to B2C
  if (!authProvider) {
    setAuthProvider('b2c');
    setAuthProviderState('b2c');
    return (
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Spinner size="huge" />
        </div>
      </FluentProvider>
    );
  }

  // Render with the appropriate MSAL provider
  const instance = authProvider === 'corporate' ? corporateInstance : b2cInstance;

  return (
    <FluentProvider theme={webLightTheme}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <MsalProvider instance={instance}>
          <NoAccessProvider onNoAccess={handleNoAccess}>
            <Router>
              <MainContent authProvider={authProvider} />
            </Router>
          </NoAccessProvider>
        </MsalProvider>
      </AppInsightsContext.Provider>
    </FluentProvider>
  );
};

/**
 * Test mode content component - bypasses authentication for Playwright testing
 * Renders the app without requiring MSAL authentication
 */
const TestModeContent: React.FC = () => {
  // Fix tabster dummy elements that violate aria-hidden-focus accessibility rule
  useFixTabsterA11y();

  const [viewMode, setViewMode] = useState<'candidate' | 'manager'>('candidate');

  // No-op handler for test mode - we don't need to handle no access errors in tests
  const handleNoAccess = useCallback(() => {
    console.log('No access handler called in test mode - ignoring');
  }, []);

  return (
    <FluentProvider theme={webLightTheme}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <NoAccessProvider onNoAccess={handleNoAccess}>
          <Router>
            <Header viewMode={viewMode} onViewModeChange={setViewMode} onReplayFRE={() => {}} showActionsInTestMode />
            <AppLayout />
          </Router>
        </NoAccessProvider>
      </AppInsightsContext.Provider>
    </FluentProvider>
  );
};

interface AppProps {
  b2cInstance: PublicClientApplication;
  corporateInstance: PublicClientApplication;
}

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App: React.FC<AppProps> = ({ b2cInstance, corporateInstance }) => {
  // In test mode, bypass authentication entirely
  if (isTestMode) {
    return <TestModeContent />;
  }

  return <AppContent b2cInstance={b2cInstance} corporateInstance={corporateInstance} />;
};

export default App;
